import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, NativeSelect } from "@/components/common/Page";
import { Spinner } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import AuthShell from "@/pages/auth/AuthShell";

const STEPS = ["Sobre você", "Onde você atende", "Suas aulas"];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    professional_name: `${user.first_name} ${user.last_name}`, area: "", subjects: "", phone: user.phone || "",
    city: "", state: "", modality: "ambas", default_price: "", default_duration: 60,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const next = async (e) => {
    e.preventDefault();
    if (step < 2) return setStep(step + 1);
    setBusy(true);
    setError("");
    try {
      await api.post("/onboarding", { ...form, subjects: form.subjects.split(",").map((s) => s.trim()).filter(Boolean),
        default_price: Number(form.default_price || 0), default_duration: Number(form.default_duration) });
      await refresh();
      toast.success("Tudo pronto! Bem-vindo ao ProfessorHub.");
      navigate("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Vamos configurar sua conta" subtitle={`Passo ${step + 1} de 3 · ${STEPS[step]}`}>
      <div className="mb-6 flex gap-2">{STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`} />)}</div>
      <form onSubmit={next} className="space-y-4" data-testid="onboarding-form">
        {step === 0 && (
          <>
            <Field label="Nome profissional"><Input value={form.professional_name} onChange={set("professional_name")} required minLength={2} data-testid="onboarding-name-input" /></Field>
            <Field label="Área de ensino"><Input placeholder="Ex.: Reforço escolar, Idiomas, Música" value={form.area} onChange={set("area")} required minLength={2} data-testid="onboarding-area-input" /></Field>
            <Field label="Disciplinas" hint="Separe por vírgula"><Input placeholder="Matemática, Física" value={form.subjects} onChange={set("subjects")} required data-testid="onboarding-subjects-input" /></Field>
          </>
        )}
        {step === 1 && (
          <>
            <Field label="Telefone"><Input value={form.phone} onChange={set("phone")} required minLength={8} data-testid="onboarding-phone-input" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Cidade" className="col-span-2"><Input value={form.city} onChange={set("city")} required minLength={2} data-testid="onboarding-city-input" /></Field>
              <Field label="Estado"><Input placeholder="SP" value={form.state} onChange={set("state")} required minLength={2} data-testid="onboarding-state-input" /></Field>
            </div>
            <Field label="Modalidade das aulas">
              <NativeSelect value={form.modality} onChange={set("modality")} data-testid="onboarding-modality-select">
                <option value="presencial">Presencial</option>
                <option value="online">Online</option>
                <option value="ambas">Ambas</option>
              </NativeSelect>
            </Field>
          </>
        )}
        {step === 2 && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Valor padrão da aula (R$)"><Input type="number" min="0" step="0.01" value={form.default_price} onChange={set("default_price")} required data-testid="onboarding-price-input" /></Field>
            <Field label="Duração padrão (min)"><Input type="number" min="15" max="480" step="5" value={form.default_duration} onChange={set("default_duration")} required data-testid="onboarding-duration-input" /></Field>
          </div>
        )}
        {error && <p className="text-sm text-destructive" data-testid="onboarding-error">{error}</p>}
        <div className="flex gap-3 pt-2">
          {step > 0 && <Button type="button" variant="outline" onClick={() => setStep(step - 1)} data-testid="onboarding-back-btn">Voltar</Button>}
          <Button type="submit" className="flex-1" disabled={busy} data-testid="onboarding-next-btn">{busy && <Spinner className="mr-2" />}{step < 2 ? "Continuar" : "Concluir"}</Button>
        </div>
      </form>
    </AuthShell>
  );
}
